'use client';

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { X, Bot } from 'lucide-react';
import { PopoverPortal } from '@/components/ui/popover-portal';

const EVENT_NAME = 'workspace:open-settings';

/** Imperative API to open the Workspace settings dialog from anywhere. */
export function openWorkspaceSettingsDialog(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

/**
 * Mounted once per workspace shell. Opens on the `workspace:open-settings`
 * window event (see {@link openWorkspaceSettingsDialog}). Esc / the X / a
 * backdrop click close it. Settings persist optimistically through the store
 * (`updateWorkspaceSettings` → PATCH /api/workspaces/[id]).
 */
export function WorkspaceSettingsDialog() {
  const t = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(EVENT_NAME, onOpen);
    return () => window.removeEventListener(EVENT_NAME, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  const close = () => setOpen(false);

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('workspace.settingsDialog.title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 220,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '8vh 4vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="glass-panel relative flex w-[min(480px,96vw)] flex-col overflow-hidden rounded-2xl shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <h2 className="text-sm font-semibold tracking-tight">
            {t('workspace.settingsDialog.title')}
          </h2>
          <div className="ml-auto" />
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={close}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-4 py-4 text-sm">
          {/* The workspace-wide "agents can use apps by default" switch used to
              live here. It was removed: a canvas-wide allow meant granting an
              app to one agent silently granted every app to every agent, and
              the per-app control that was supposed to be the real boundary sat
              inert behind it. Access is now per app, and this section says so
              rather than leaving the dialog blank for anyone who came looking
              for the switch. */}
          <section className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-primary/10 p-1.5 text-primary">
              <Bot className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{t('workspace.settingsDialog.agentAccessPerApp')}</p>
              <p className="mt-0.5 text-[12px] text-muted-foreground leading-snug">
                Each app keeps its own list of agents allowed to drive it. Open
                the app&apos;s settings to see and change its list, or use Drive
                to hand an app to an agent. An agent that asks for an app it
                has not been given will ask you first.
              </p>
            </div>
          </section>
        </div>
      </div>
    </PopoverPortal>
  );
}
